import { CheckBox } from "devextreme-react/check-box";
import { SelectBox } from "devextreme-react/select-box";
import { RequiredRule, Validator } from "devextreme-react/validator";
import { DateBox } from 'devextreme-react/date-box';
import { useContext, useState } from 'react';
import { useGetLookupValuesByType } from '../../../../api/lookup';
import { useGetCompanyUsers } from '../../../../api/users';
import { DateWithTimeframeBox } from '../../../../components';
import { useAuth } from '../../../../contexts/auth';
import { Field, Panel } from '../../../../layouts';
import { AR, BusinessFlow, OrderStatus, ProductLine, ROType, ScriptFileType } from '../../../../types/enums';
import { disablePastDates, formatShortDate, TimeframeType } from '../../../../utils/date-utils';
import { RefetchScope, RetailOrderContext } from '../context/RetailOrderContext';
import CallScriptComp from "../../call-script/component/CallScriptComp";
import { useGetCompany } from "../../../../api/company";

type FieldSetup = {
	name: string,
	readOnly: boolean,
	required: boolean
}

export default function TemplateDatesPanel() {
	const { order, modifyOrder, refetchOrder } = useContext(RetailOrderContext);

	const { user } = useAuth();
	const dealer = useGetCompany(order.details.dealerCompanyID);
	const companyUsers = useGetCompanyUsers(user?.companyId!);
	const reschedulReasons = useGetLookupValuesByType('TemplateRescheduleReason');

	const [templateNotRequired, setTemplateNotRequired] = useState(
		order.details.resolvedARs.includes(AR.CfiToEnterTemplateDate) && !order.details.enterTemplateDate);

	const isOrderReadonly =
		order.details.orderStatusID === OrderStatus.Closed ||
		order.details.orderStatusID === OrderStatus.Cancelled ||
		order.details.orderStatusID === OrderStatus.Pending ||
		order.details.orderStatusID === OrderStatus.Hold;

	const isMeasureOrder = order.details.roTypeID === ROType.Measure || order.details.roTypeID === ROType.CabinetMeasureOrder;
	const isVanityOrder = order.details.roTypeID === ROType.Vanity || order.details.roTypeID === ROType.VanityAddOn;
	const isCabinetOrder = order.details.roTypeID === ROType.CabinetInstallOrder || order.details.roTypeID === ROType.CabinetMeasureOrder;
	const isPlumbing = order.details.roTypeID === ROType.Plumbing;

	let customerContactDate = { name: 'Customer Contact Date', readOnly: true, required: false } as FieldSetup;
	let readyToMeasureOrTemplateDate = {
		name: isMeasureOrder ? 'Ready to Measure Date' : 'Ready to Measure/Template Date',
		readOnly: true, required: false
	} as FieldSetup;
	let measureDate = {
		name: isMeasureOrder ? 'Scheduled Measure Date' : 'Measure Date',
		readOnly: true, required: false
	} as FieldSetup;
	let enterTemplateDate = { name: 'Template Date', readOnly: true, required: false } as FieldSetup;
	let measurer = { name: 'Measurer', readOnly: true, required: false } as FieldSetup;
	let templater = { name: 'Templater', readOnly: true, required: false } as FieldSetup;
	let templateRescheduleReasonID = { name: 'Reschedule Reason', readOnly: true, required: false } as FieldSetup;

	if (order.details.activeARs.includes(AR.CfiToContactCustomer)) {
		customerContactDate.readOnly = false;
	}

	if (order.details.activeARs.includes(AR.CfiToContactCustomer)) {
		customerContactDate.readOnly = false;
	}

	if (order.details.activeARs.includes(AR.CfiToEnterReadyToMeasureOrTemplateDate) ||
		order.details.resolvedARs.includes(AR.CfiToEnterReadyToMeasureOrTemplateDate) &&
		!order.details.scheduledInstallDate && !order.details.actualMeasureDate) {
		readyToMeasureOrTemplateDate.readOnly = false;
	}

	if (isMeasureOrder) {
		if (order.details.activeARs.includes(AR.CfiToEnterTemplateDate) ||
			order.details.activeARs.includes(AR.CfiToEnterActualMeasureDate) ||
			(order.details.activeARs.includes(AR.CfiToConfirmOrChangeOrder) &&
				dealer.data?.businessFlowID != BusinessFlow.Lowes && !isCabinetOrder)) {
			measureDate.readOnly = false;
			measurer.readOnly = false;
			templateRescheduleReasonID.readOnly = false;
			templateRescheduleReasonID.required = !!order.details.measureDate &&
				order.details.measureDate !== order.originalDetails.measureDate;
		}
	}
	else {
		if (order.details.activeARs.includes(AR.CfiToEnterTemplateDate) ||
			order.details.activeARs.includes(AR.CfiToConfirmOrChangeOrder)) {
			measureDate.readOnly = false;
			enterTemplateDate.readOnly = false;
			templater.readOnly = false;
			templateRescheduleReasonID.readOnly = false;
			templateRescheduleReasonID.required = !!order.details.enterTemplateDate &&
				order.details.enterTemplateDate !== order.originalDetails.enterTemplateDate;
		}
	}

	customerContactDate.required = order.details.activeARs.includes(AR.CfiToContactCustomer);

	readyToMeasureOrTemplateDate.required = !readyToMeasureOrTemplateDate.readOnly;

	measureDate.required = isMeasureOrder && !measureDate.readOnly;

	enterTemplateDate.required = !enterTemplateDate.readOnly;

	return (
		<Panel header={'Dates'}>
			<Field label={customerContactDate.name} >
				<DateBox value={order.details["customerContactDate"]}
					onValueChanged={(e) => modifyOrder({ ...order.details, customerContactDate: e.value })}
					readOnly={customerContactDate.readOnly || isOrderReadonly}
					max={new Date()}
					className={customerContactDate.required ? 'required' : ''}>
					<Validator>
						{customerContactDate.required && order.modified &&
							<RequiredRule message={customerContactDate.name + ' is required'} />}
					</Validator>
				</DateBox>
				{
					(order.details.activeARs.includes(AR.CfiToContactCustomer) || order.details.resolvedARs.includes(AR.CfiToContactCustomer)) &&
					dealer.data?.businessFlowID === BusinessFlow.Lowes &&
					<CallScriptComp
						fileType={ScriptFileType.InitialContact}
						retailOrderID={order.details.retailOrderID}
						retailOrderNumber={order.details.roNumber}
						fileName={"Initial Contact"}
						fileAttachment={order.details.files}
						submitCallback={() => refetchOrder(RefetchScope.Files)}
					/>
				}
				{
					(order.details.activeARs.includes(AR.CfiToContactCustomer) || order.details.resolvedARs.includes(AR.CfiToContactCustomer)) &&
					isCabinetOrder &&
					<CallScriptComp
						fileType={ScriptFileType.CabinetInstallerScheduleMeasurement}
						retailOrderID={order.details.retailOrderID}
						retailOrderNumber={order.details.roNumber}
						fileName={"Cabinet Installer Schedule Measurement"}
						fileAttachment={order.details.files}
						submitCallback={() => refetchOrder(RefetchScope.Files)}
					/>
				}
			</Field>
			<Field label={readyToMeasureOrTemplateDate.name}
				visible={!isVanityOrder}>
				<DateBox
					inputAttr={{ 'data-testid': 'dtb-ready-to-measure-template-date' }}
					value={order.details.readyToMeasureOrTemplateDate}
					onValueChanged={(e) => modifyOrder({ ...order.details, readyToMeasureOrTemplateDate: e.value })}
					readOnly={readyToMeasureOrTemplateDate.readOnly || isOrderReadonly}
					className={readyToMeasureOrTemplateDate.required ? 'required' : ''}>
					<Validator>
						{readyToMeasureOrTemplateDate.required && order.modified &&
							<RequiredRule message={readyToMeasureOrTemplateDate.name + ' is required'} />}
					</Validator>
				</DateBox>
				{
					(order.details.activeARs.includes(AR.CfiToEnterReadyToMeasureOrTemplateDate) || order.details.resolvedARs.includes(AR.CfiToEnterReadyToMeasureOrTemplateDate)) &&
					!order.details.resolvedARs.includes(AR.CfiToContactCustomer) &&
					dealer.data?.businessFlowID === BusinessFlow.Lowes &&
					<CallScriptComp
						fileType={ScriptFileType.InitialContact}
						retailOrderID={order.details.retailOrderID}
						retailOrderNumber={order.details.roNumber}
						fileName={"Initial Contact"}
						fileAttachment={order.details.files}
						submitCallback={() => refetchOrder(RefetchScope.Files)}
					/>
				}
				{
					(order.details.activeARs.includes(AR.CfiToEnterReadyToMeasureOrTemplateDate) || order.details.activeARs.includes(AR.CfiToEnterTemplateDate)) ||
					(order.details.resolvedARs.includes(AR.CfiToEnterReadyToMeasureOrTemplateDate) && !order.details.resolvedARs.includes(AR.CfiToContactCustomer)) &&
					isCabinetOrder &&
					<CallScriptComp
						fileType={ScriptFileType.CabinetInstallerScheduleMeasurement}
						retailOrderID={order.details.retailOrderID}
						retailOrderNumber={order.details.roNumber}
						fileName={"Cabinet Installer Schedule Measurement"}
						fileAttachment={order.details.files}
						submitCallback={() => refetchOrder(RefetchScope.Files)}
					/>
				}
			</Field>
			<Field label={measureDate.name} >
				<DateWithTimeframeBox elementAttr={{ 'data-testid': 'dttb-measure-date' }}
					value={order.details.measureDate}
					timeframeType={TimeframeType.Short}
					timeRequired={true}
					onValueChanged={(e) => modifyOrder({ ...order.details, measureDate: e.value })}
					disabledDates={(args) => disablePastDates(args.date, order.originalDetails.measureDate)}
					readOnly={measureDate.readOnly || isOrderReadonly}
					className={measureDate.required ? 'required' : ''}>
					<Validator>
						{measureDate.required && order.modified && (order.details.readyToMeasureOrTemplateDate === order.originalDetails.readyToMeasureOrTemplateDate) &&
							<RequiredRule message={measureDate.name + ' is required'} />}
					</Validator>
				</DateWithTimeframeBox>
			</Field>
			<Field label={enterTemplateDate.name}
				visible={!isMeasureOrder} >
				<DateWithTimeframeBox elementAttr={{ 'data-testid': 'dttb-enter-template-date' }}
					timeframeType={TimeframeType.Short}
					timeRequired={true}
					value={order.details.enterTemplateDate}
					onValueChanged={(e) => modifyOrder({ ...order.details, enterTemplateDate: e.value })}
					readOnly={enterTemplateDate.readOnly || isOrderReadonly}
					className={enterTemplateDate.required && !templateNotRequired ? 'required' : ''}
				>
					<Validator>
						{enterTemplateDate.required && order.modified && !templateNotRequired && (order.details.readyToMeasureOrTemplateDate === order.originalDetails.readyToMeasureOrTemplateDate) &&
							<RequiredRule message={enterTemplateDate.name + ' is required'} />}
					</Validator>
				</DateWithTimeframeBox>
				{
					(order.details.activeARs.includes(AR.CfiToEnterTemplateDate) || order.details.resolvedARs.includes(AR.CfiToEnterTemplateDate)) &&
					!isMeasureOrder &&
					dealer.data?.businessFlowID === BusinessFlow.Lowes &&
					order.details.productLineID !== ProductLine.SmallProjects &&
					<div className='call-script-container'>
						<CallScriptComp
							fileType={ScriptFileType.TemplateChecklist}
							retailOrderID={order.details.retailOrderID}
							retailOrderNumber={order.details.roNumber}
							fileName={"Template Checklist"}
							fileAttachment={order.details.files}
							submitCallback={() => refetchOrder(RefetchScope.Files)}
						/>
					</div>
				}
			</Field>
			<Field label={'Measurer'} visible={isMeasureOrder} >
				<SelectBox
					value={order.details.measurer}
					showClearButton={true}
					readOnly={measurer.readOnly || isOrderReadonly}
					valueExpr={'userName'}
					displayExpr={'fullName'}
					onValueChanged={(e) => modifyOrder({ ...order.details, measurer: e.value })}
					dataSource={companyUsers?.data.filter(x => x.groups?.includes('Measure/Template') &&
						(!x.inactive || x.userName === order.details.measurer))} />
			</Field>
			<Field label={'Templater'} visible={!isVanityOrder && !isMeasureOrder} >
				<SelectBox elementAttr={{ 'data-testid': 'dd-templater' }}
					value={order.details.templater}
					showClearButton={true}
					readOnly={templater.readOnly || isOrderReadonly}
					valueExpr={'userName'}
					displayExpr={'fullName'}
					onValueChanged={(e) => modifyOrder({ ...order.details, templater: e.value })}
					dataSource={companyUsers?.data.filter(x => x.groups?.includes('Measure/Template') &&
						(!x.inactive || x.userName === order.details.templater))} />
			</Field>
			<Field label={templateRescheduleReasonID.name}
				visible={((!!order.originalDetails.measureDate && isMeasureOrder) ||
					(!!order.originalDetails.enterTemplateDate && !isMeasureOrder)) && !isCabinetOrder}>
				<SelectBox
					value={order.details.templateRescheduleReasonID}
					readOnly={templateRescheduleReasonID.readOnly || isOrderReadonly}
					showClearButton={true}
					onValueChanged={(e) => modifyOrder({ ...order.details, templateRescheduleReasonID: e.value })}
					dataSource={reschedulReasons.data}
					valueExpr={'lookupID'}
					displayExpr={'displayValue'}
					className={templateRescheduleReasonID.required ? 'required' : ''}>
					<Validator>
						{templateRescheduleReasonID.required && order.modified &&
							<RequiredRule message={templateRescheduleReasonID.name + ' is required'} />}
					</Validator>
				</SelectBox>
			</Field>
			<Field label={"Template Not Required"}
				visible={isVanityOrder && !isOrderReadonly && enterTemplateDate.required}>
				<CheckBox value={templateNotRequired}
					onValueChanged={(e) => {
						setTemplateNotRequired(e.value);
						modifyOrder(order.details);
					}}>

				</CheckBox>
				{
					!!order.details.vanityOrderFormUrl &&
					<div className='vanity-form-hint'>
						Note: Please review the
						<a href={order.details.vanityOrderFormUrl} target="_blank" rel="noreferrer">Vanity Order Form</a>
						before clicking Save Changes.
					</div>
				}
			</Field>

			<Field label={'Cabinet Install Date'} visible={!isCabinetOrder}>
				{formatShortDate(order.details.cabinetDate)}
			</Field>

		</Panel>
	);
}

